import i18next from 'i18next';

import { htmlUtils } from './htmlUtils';

export const DEFAULT_HTML_FONT_SIZE = '10pt';
export const DEFAULT_HTML_FONT_FAMILY = 'Arial, sans-serif';
export const SIGNATURE_SELECTOR =
  ':is(.signature, .email-signature, [fr-original-class*=signature])';

export const EMPTY_LINE = '<br/>';
export const EMAIL_SIGNATURE_START = '<div class="email-signature">';
export const SIGNATURE_END = '</div>';

export const getSignatureLabel = (): string => {
  return i18next?.t?.('INPUTS.SIGNATURE') || 'Signature';
};

export const getSignatureDividerHTML = (): string => {
  const signatureLabel = getSignatureLabel();
  return `<div class="signature-divider"><span class="signature-label">${signatureLabel}</span><hr class="signature-line"/></div>`;
};

export function createElementFromHTML(htmlString: string) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, 'text/html');
  return doc.documentElement.querySelector('body');
}

function applyInlineStyles(input: string) {
  if (!input) return input;
  const masterDiv = createElementFromHTML(input);
  for (let i = 0; i < masterDiv.children.length; i++) {
    const child = masterDiv.children[i] as HTMLElement;
    if (!child.style?.fontSize) {
      child.style.fontSize = DEFAULT_HTML_FONT_SIZE;
    }
    if (!child.style?.fontFamily) {
      child.style.fontFamily = DEFAULT_HTML_FONT_FAMILY;
    }
  }
  return masterDiv.innerHTML;
}

function toApi(html: string) {
  const masterDiv = createElementFromHTML(html);
  const nodes = masterDiv.querySelectorAll('[fr-original-class*=textmarke]');
  nodes.forEach((node) => {
    let current = node.firstChild;

    if (htmlUtils.isText(current)) {
      const classNames =
        node.getAttribute('fr-original-class')?.split(' ') || [];
      classNames.forEach((className) => {
        if (!node.className.includes(className)) node.classList.add(className);
      });
      return;
    }

    // check if this has className textmarke if yes just remove it
    while (current) {
      current = current.firstChild;
      if (htmlUtils.isText(current)) {
        const span = document.createElement('span');
        span.innerHTML = current.textContent;
        span.classList.add('api-cleanable');

        const dataItem = node.getAttribute('data-item');
        const dataName = node.getAttribute('data-name');

        const hasTextMarkData = Boolean(dataItem) && Boolean(dataName);
        if (hasTextMarkData) span.classList.add('textmarke');

        if (dataItem) span.setAttribute('data-item', dataItem);
        if (dataName) span.setAttribute('data-name', dataName);
        current.parentElement.innerHTML = span.outerHTML;
      }
    }
  });

  const disabledNodes = masterDiv.querySelectorAll('[data-item-disabled]');
  disabledNodes.forEach((node) => {
    const current = node.firstChild;

    if (htmlUtils.isText(current)) {
      const dataItem = node.getAttribute('data-item');
      const dataName = node.getAttribute('data-name');
      const hasTextMarkData = Boolean(dataItem) && Boolean(dataName);
      if (!hasTextMarkData) {
        node.className = node.className.replace(/textmarke/, '');
      }
      return;
    }
  });

  return applyInlineStyles(masterDiv.innerHTML);
}

function toFroala(html: string) {
  const masterDiv = createElementFromHTML(html);
  const nodes = masterDiv.querySelectorAll('[class*=api-cleanable]');
  nodes.forEach((node) => {
    node.parentElement.innerHTML = node.innerHTML;
  });

  masterDiv.childNodes.forEach((x: HTMLElement) => {
    if (htmlUtils.hasNoText(x)) {
      x.remove();
    }
  });

  return applyInlineStyles(masterDiv.innerHTML);
}

function cleanUpTextmarks(html: string) {
  const doc = createElementFromHTML(html);
  const textmarks = doc.querySelectorAll('.textmarke');
  for (const textmark of textmarks) {
    textmark.classList.remove('fr-deletable', 'blockinput');
    textmark.removeAttribute('style');
  }

  return doc.outerHTML;
}

function replaceSignature(
  input: string,
  newSignature: string,
  options = { includeDivider: true, useEmailSignature: false }
) {
  // If input is empty, add an empty line before adding signature
  const isEmpty = !input || input.trim() === '' || input.trim() === '<p></p>';
  if (isEmpty) {
    input = EMPTY_LINE;
  }

  const signatureWithAttributedImages =
    addSignatureAttributeToImages(newSignature);

  const masterDiv = createElementFromHTML(input);
  const nextEmailStartMarker = masterDiv.querySelector(
    'hr[data-next-email-start]'
  );

  // Find all signatures, but only consider those BEFORE the previous email marker
  // to avoid replacing signatures in quoted/previous email content
  const allSignatureEls = masterDiv.querySelectorAll(SIGNATURE_SELECTOR);
  let signatureEls: Element[] = [];

  if (nextEmailStartMarker) {
    // Filter out signatures that come after the marker (in quoted email)
    for (let i = 0; i < allSignatureEls.length; i++) {
      const sig = allSignatureEls[i];
      const markerPosition = nextEmailStartMarker.compareDocumentPosition(sig);
      if (markerPosition & Node.DOCUMENT_POSITION_PRECEDING) {
        signatureEls.push(sig);
      }
    }
  } else {
    signatureEls = Array.from(allSignatureEls);
  }

  const oldSignature = signatureEls?.length
    ? signatureEls[signatureEls.length - 1]
    : null;

  if (oldSignature) {
    if (signatureWithAttributedImages) {
      oldSignature.innerHTML = signatureWithAttributedImages;
    } else {
      oldSignature.remove();
    }
    return applyInlineStyles(masterDiv.innerHTML);
  }

  const signatureClass = options.useEmailSignature
    ? 'email-signature'
    : 'signature';

  if (oldSignature) {
    // Clear the existing signature content
    oldSignature.innerHTML = '';

    // Add divider if requested
    if (options.includeDivider) {
      const dividerHTML = getSignatureDividerHTML();
      const divider = document.createElement('div');
      divider.innerHTML = dividerHTML;
      oldSignature.appendChild(divider.firstChild);
    }

    // Add the signature content
    const contentDiv = document.createElement('div');
    contentDiv.className = 'signature-content';
    contentDiv.innerHTML = signatureWithAttributedImages;
    oldSignature.appendChild(contentDiv);

    // Update the signature class
    oldSignature.className = signatureClass;
  } else {
    const div = document.createElement('div');
    div.className = signatureClass;

    // Add divider if requested
    if (options.includeDivider) {
      const dividerHTML = getSignatureDividerHTML();
      const divider = document.createElement('div');
      divider.innerHTML = dividerHTML;
      div.appendChild(divider.firstChild);
    }

    // Add the signature content
    const contentDiv = document.createElement('div');
    contentDiv.className = 'signature-content';
    contentDiv.innerHTML = signatureWithAttributedImages;
    div.appendChild(contentDiv);

    if (nextEmailStartMarker) {
      nextEmailStartMarker.parentElement.insertBefore(
        div,
        nextEmailStartMarker
      );
    } else {
      masterDiv.append(div);
    }
  }

  return applyInlineStyles(masterDiv.innerHTML);
}

function asSignature(
  input: string,
  options = { includeDivider: true, useEmailSignature: false }
) {
  // Don't create signature structure if there's no signature content
  if (!input) {
    return '';
  }

  const inputWithAttributedImages = addSignatureAttributeToImages(input);

  const signatureClass = options.useEmailSignature
    ? 'email-signature'
    : 'signature';
  const signatureStart = `<div class="${signatureClass}">`;
  const dividerHTML = options.includeDivider ? getSignatureDividerHTML() : '';
  const contentDiv = `<div class="signature-content">${inputWithAttributedImages}</div>`;

  return applyInlineStyles(
    `${signatureStart}${dividerHTML}${contentDiv}${SIGNATURE_END}`
  );
}

function disableTextmarks(input: string) {
  return input
    .replace('class="signature"', '')
    .replace(/data-item=/gm, 'data-item-disabled=')
    .replace(/data-name=/gm, 'data-name-disabled=');
}

function removeTextmarkStyle(input: string) {
  const masterDiv = createElementFromHTML(input);
  const nodes = masterDiv.querySelectorAll('[fr-original-class*=textmarke]');
  nodes.forEach((node) => {
    (node as HTMLElement).style.cssText = '';
  });
  return masterDiv.innerHTML;
}

function addSignatureAttributeToImages(input: string) {
  const inputElement = createElementFromHTML(input);

  const images = inputElement.querySelectorAll('img');
  images.forEach((img) => img.setAttribute('data-signature-image', 'true'));

  return inputElement.innerHTML;
}

function extractImageDimensionsFromStyle(input: string) {
  if (!input) return input;
  const masterDiv = createElementFromHTML(input);
  const images = masterDiv.querySelectorAll('img');

  images.forEach((img) => {
    const htmlImg = img as HTMLElement;
    const style = htmlImg.style;
    const styleWidth = style.width;
    const styleHeight = style.height;

    // Extract width from style if present
    // Only extract pixel values for email compatibility
    if (styleWidth) {
      let widthValue: string | null = null;
      // Check if it's a pixel value (ends with 'px')
      if (styleWidth.toLowerCase().endsWith('px')) {
        widthValue = styleWidth.replace(/px/gi, '').trim();
      } else if (!isNaN(Number(styleWidth)) && styleWidth.trim() !== '') {
        // If it's just a number, use it directly
        widthValue = styleWidth.trim();
      }
      if (widthValue && !isNaN(Number(widthValue))) {
        img.setAttribute('width', widthValue);
      }
    }

    // Extract height from style if present
    // Only extract pixel values for email compatibility
    if (styleHeight) {
      let heightValue: string | null = null;
      // Check if it's a pixel value (ends with 'px')
      if (styleHeight.toLowerCase().endsWith('px')) {
        heightValue = styleHeight.replace(/px/gi, '').trim();
      } else if (!isNaN(Number(styleHeight)) && styleHeight.trim() !== '') {
        // If it's just a number, use it directly
        heightValue = styleHeight.trim();
      }
      if (heightValue && !isNaN(Number(heightValue))) {
        img.setAttribute('height', heightValue);
      }
    }

    //wrap image in div if it ihass display: block
    const computedDisplay = img.style.display;

    if (computedDisplay.toLowerCase() === 'block') {
      const wrapper = document.createElement('div');
      img.parentElement?.insertBefore(wrapper, img);
      wrapper.appendChild(img);
    }
  });

  return masterDiv.innerHTML;
}

export const htmlParser = {
  toApi,
  toFroala,
  asSignature,
  replaceSignature,
  applyInlineStyles,
  disableTextmarks,
  removeTextmarkStyle,
  cleanUpTextmarks,
  extractImageDimensionsFromStyle,
};
